import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
  TablePagination,
} from "@mui/material";
import { CirclePlus, Search, Upload } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import {
  getDukaStocks,
  createDukaStock,
  updateDukaStock,
  deleteDukaStock,
  bulkUploadStock
} from "../../api/DukaStock/api";
import useDukaStockPermissions from "./hooks/useDukaStockPermissions";
import DataTable from "../../components/DataTable";
import DukaStockForm from "../../components/shared/forms/DukaStockForm";
import { AuthContext } from "../../contexts/AuthContext";
import Toast from "../../components/shared/Toast";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { extractErrorMessages } from "../../utils/errorHandler";
import UploadModal from "../../components/UploadModal";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const DukaStockManagement = () => {
  const { loading: loadingPermissions, error: permissionsError, can } = useDukaStockPermissions();
  const { user } = useContext(AuthContext);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stockToDelete, setStockToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const response = await getDukaStocks({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      if (response?.data) {
        const transformedData = response.data.map((stock) => ({
          id: stock.id,
          duka: stock.duka_name,
          product: stock.product_name,
          availableQuantity: stock.available_quantity,
          lastUpdated: stock.last_updated,
          original: stock,
        }));
        setStocks(transformedData);
        setTotal(response.total);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching Duka Stock:", err);
      setError("Failed to load Duka Stock. Please try again later.");
      setStocks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loadingPermissions && can.viewDukaStock()) {
      fetchStocks();
    }
  }, [loadingPermissions, page, rowsPerPage, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const transformStockData = (data) => {
    return {
      duka: data.duka,
      product: data.product,
      available_quantity: parseInt(data.available_quantity, 10),
    };
  };

  const handleCreateStock = async (data) => {
    try {
      if (!data.duka || !data.product || data.available_quantity === undefined) {
        setError("Please fill in all required fields");
        return;
      }
      const transformedData = transformStockData(data);
      await createDukaStock(transformedData);
      showSuccessToast("Duka Stock created successfully");
      setOpenModal(false);
      fetchStocks();
    } catch (error) {
      const messages = extractErrorMessages(error);
      messages.forEach((message) => showErrorToast(message));
    }
  };

  const handleUpdateStock = async (id, data) => {
    try {
      if (!data.duka || !data.product || data.available_quantity === undefined) {
        setError("Please fill in all required fields");
        return;
      }
      const transformedData = transformStockData(data);
      await updateDukaStock(id, transformedData);
      setOpenModal(false);
      setSelectedStock(null);
      fetchStocks();
      showSuccessToast("Duka Stock updated successfully");
    } catch (error) {
      const messages = extractErrorMessages(error);
      messages.forEach((message) => showErrorToast(message));
    }
  };

  const handleDeleteStock = async () => {
    if (!stockToDelete) return;
    try {
      await deleteDukaStock(stockToDelete.id);
      setDeleteModalOpen(false);
      setStockToDelete(null);
      fetchStocks();
      showSuccessToast("Duka Stock deleted successfully");
    } catch (error) {
      console.error("Error deleting Duka Stock:", error);
      showErrorToast(
        error.response?.data?.message ||
          "Failed to delete Duka Stock. Please try again."
      );
    }
  };

  const handleSubmit = (data) => {
    if (selectedStock) {
      handleUpdateStock(selectedStock.id, data);
    } else {
      handleCreateStock(data);
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
  };

  const handleCSVUpload = async () => {
    if (!uploadFile) {
      showErrorToast("Please select a CSV file", "Validation Error");
      return;
    }
    try {
      await bulkUploadStock(uploadFile);
      showSuccessToast("Bulk upload successful");
      setOpenUploadModal(false);
      fetchStocks();
    } catch (err) {
      showErrorToast("Bulk upload failed", "Error");
    }
  };

  const filteredStocks = stocks.filter((stock) =>
    Object.values(stock).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "duka", label: "Duka" },
    { id: "product", label: "Product" },
    { id: "availableQuantity", label: "Available Quantity" },
    { id: "lastUpdated", label: "Last Updated" },
  ];

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Duka Stock
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          {can.createDukaStock() && (
            <Button
              variant="contained"
              startIcon={<Upload size={18} />}
              onClick={() => setOpenUploadModal(true)}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": {
                  backgroundColor: "#1a614d !important",
                },
              }}
            >
              Upload CSV
            </Button>
          )}
          {can.createDukaStock() && (
            <Button
              variant="contained"
              startIcon={<CirclePlus size={18} />}
              onClick={() => {
                setSelectedStock(null);
                setOpenModal(true);
              }}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Add Duka Stock
            </Button>
          )}
        </Box>
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={filteredStocks}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        totalRows={total}
        onEdit={
          can.editDukaStock()
            ? (rowData) => {
                setSelectedStock(rowData.original);
                setOpenModal(true);
              }
            : undefined
        }
        onDelete={
          can.deleteDukaStock()
            ? (rowData) => {
                setStockToDelete(rowData.original);
                setDeleteModalOpen(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        deleteIcon={<DeleteIcon />}
        editTooltip="Edit"
        deleteTooltip="Delete"
        editDisabled={!can.editDukaStock()}
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ),
        }}
      />

      {(can.createDukaStock() || can.editDukaStock()) && (
        <Modal
          open={openModal}
          onClose={() => {
            setSelectedStock(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <DukaStockForm
              initialData={selectedStock || null}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedStock(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}
      <UploadModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        onFileChange={handleFileChange}
        onUpload={handleCSVUpload}
      />

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteStock}
        title="Delete Duka Stock"
        description="Are you sure you want to delete this Duka Stock?"
      />

      <Toast />
    </Box>
  );
};

export default DukaStockManagement;